import {
	useMemo,
	useCallback,
	useEffect,
	useLayoutEffect,
	type MutableRefObject,
	useState,
} from 'react';

import { type FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';
import type { ExperienceAttributes } from '@confluence/experience-tracker/entry-points/ExperienceEvent.ts';
import type { ExperienceTrackerAPI } from '@confluence/experience-tracker/entry-points/ExperienceTracker.ts';
import type { ContentStatus } from '@confluence/content-types-header/entry-points/ContentStatus.tsx';
import type { MoreActions } from '@confluence/content-types-header/entry-points/MoreActions.tsx';
import { useSidePanelStateActions } from '@confluence/side-panel-state-container';
import type { ContentTypesObjectSidebarService } from '@confluence/content-types-bridge/entry-points/content-types-object-sidebar-service.ts';
import type {
	ContentTypeBridgeAPI,
	ConfluenceSidebarState,
} from '@confluence/content-types-bridge/entry-points/content-types-shell-types.ts';
import type { ContentTypesFlagProps } from '@confluence/content-types-bridge/entry-points/content-types-flag-service.ts';
import { ContentTypesFlagService } from '@confluence/content-types-bridge/entry-points/content-types-flag-service.ts';
import { contentMoreActionsService } from '@confluence/content-types-bridge/entry-points/content-more-actions-service.ts';
import { contentCollaboratorsService } from '@confluence/content-types-bridge/entry-points/content-collaborators-service.ts';
import { contentBoardStatusService } from '@confluence/content-types-bridge/entry-points/content-board-status-service.ts';
import {
	type ResolvedCommentHandler,
	useCommentService,
} from '@confluence/content-types-bridge-comments';
import { useDetailsPanelService } from '@confluence/whiteboard-common/entry-points/useDetailsPanelService.ts';
import { useThemingService } from '@confluence/whiteboard-common/entry-points/useThemingService.ts';
import {
	useIsObjectSidebarShown,
	useObjectSidebarActions,
	usePanel,
} from '@confluence/object-sidebar-api/entry-points/useObjectSidebar.ts';
import type { CanvasCollaborators } from '@confluence/team-presence/entry-points/presenceTypes.ts';
import { contentTypesTitleServiceSingleton } from '@confluence/content-types-header-base';
import { useIsReactionsEnabled } from '@confluence/comments-hooks/entry-points/useIsReactionsEnabled.ts';

import type { DatabaseOnboardingService } from '../services/database-onboarding-service.ts';
import { DatabaseOrgDataService } from '../services/database-org-data-service.ts';
import {
	useIsDatabaseFeatureEnabled,
	type DatabaseFeatureGate,
} from './useIsDatabaseFeatureEnabled.ts';
import { useDatabaseRovoContentBridge } from './useDatabaseRovoContentBridge.ts';
import { usePublishDatabaseReferencingContent } from './usePublishDatabaseReferencingContent.ts';

type ObjectSidebarBridgeMethods = {
	/*
	 * Hook for the database iframe to show or hide the object sidebar control in CFE
	 */
	updateObjectSidebarControlState: (isShown: boolean) => void;
	/**
	 * Hook for hiding the object sidebar panels (details, comments, etc.) in CFE
	 */
	hideObjectSidebarPanel: () => void;
	/**
	 * Registers a callback from canvas that CFE fires when the object sidebar panels change visibility
	 */
	registerChangeObjectSidebarShownHandler: (cb: (isShown?: boolean) => void) => void;
};

interface DatabaseBridgeAPI extends ContentTypeBridgeAPI {
	onboardingService: DatabaseOnboardingService;
	objectSidebarMethods: ObjectSidebarBridgeMethods;
	updateBoardStatus: (status: ContentStatus) => void;
	onThemeChange: (callback: () => void) => void;
	registerOnReopenResolvedComment: (cb: ResolvedCommentHandler) => void;
	updateMoreActions: (items: MoreActions) => void;
	/*
	 * A hook for the database iframe to handle when database body loads successfully.
	 */
	onContentLoadSucceeded: (attributes: ExperienceAttributes) => void;
	/**
	 * A hook for the database iframe to handle when database body loads unsuccessfully.
	 */
	onContentLoadFailed: (error: Error, attributes: ExperienceAttributes) => void;
	/**
	 * This should be called when fetching the current loaded version name (will return null on latest version)
	 * @returns {string | null} The name of the current version, as it should be shown in CFE
	 */
	getVersionName: () => string | null;
	/**
	 * A hook that enables the database iframe to dismiss the Confluence side panel which currently houses version history
	 * This can be reused in the future when more features are added to the side panel
	 */
	onCloseConfluenceSidePanel: () => void | undefined;
	/**
	 * Ref that holds the DB spotlight close callback
	 */
	databaseSpotlightCloseRef: MutableRefObject<(() => void) | null>;
	/**
	 * Returns the orgId
	 */
	getOrgId: () => string | undefined;
	/**
	 * Publishes the content selected in a database for rovo chat
	 */
	publishDatabaseReferencingContent: (selectedElementIds: string[]) => void;
}

// NOTE: Please start moving away from adding new methods here without adding them to @confluence/content-types-bridge
declare global {
	interface Window {
		databaseBridge?: {
			onboardingService: DatabaseOnboardingService;
			objectSidebarMethods: ObjectSidebarBridgeMethods;
			updateBoardStatus: (status: ContentStatus) => void;
			updateMoreActions: (items: MoreActions) => void;
			onThemeChange: (callback: () => void) => void;
			registerOnReopenResolvedComment: (cb: ResolvedCommentHandler) => void;
			/*
			 * A hook for the database iframe to handle when database body loads successfully.
			 */
			onContentLoadSucceeded: (attributes: ExperienceAttributes) => void;
			/**
			 * A hook for the database iframe to handle when database body loads unsuccessfully.
			 */
			onContentLoadFailed: (error: Error, attributes: ExperienceAttributes) => void;
			/**
			 * This should be called when fetching the current loaded version name (will return null on latest version)
			 * @returns {string | null} The name of the current version, as it should be shown in CFE
			 */
			getVersionName: () => string | null;
			/**
			 * A hook that enables the database iframe to dismiss the Confluence side panel which currently houses version history
			 * This can be reused in the future when more features are added to the side panel
			 */
			onCloseConfluenceSidePanel: () => void | undefined;
			/**
			 * Ref that holds the DB spotlight close callback
			 */
			databaseSpotlightCloseRef: MutableRefObject<(() => void) | null>;
			/**
			 * Returns the orgId
			 */
			getOrgId: () => string | undefined;
			/**
			 * Publishes the content selected in a database for rovo chat
			 */
			publishDatabaseReferencingContent: (selectedElementIds: string[]) => void;
		};
	}
}

interface UseDatabaseBridgeMethodsProps {
	contentId: string;
	experienceTracker: ExperienceTrackerAPI;
	onboardingService: DatabaseOnboardingService;
	orgId?: string;
	segmentMetric: {
		stop: () => void;
		cancel: () => void;
	};
	experienceName: string;
	versionHistoryExperienceName: string;
	versionId: string | null;
	getVersionName: () => string | null;
	objectSidebarService: ContentTypesObjectSidebarService;
	databaseSpotlightCloseRef: MutableRefObject<(() => void) | null>;
	flags: FlagsStateContainer;
}

export const useDatabaseBridgeMethods = ({
	contentId,
	experienceTracker,
	onboardingService,
	orgId,
	segmentMetric,
	experienceName,
	versionHistoryExperienceName,
	versionId,
	getVersionName,
	objectSidebarService,
	databaseSpotlightCloseRef,
	flags,
}: UseDatabaseBridgeMethodsProps) => {
	const { themingService } = useThemingService();
	const { hideSidePanel } = useSidePanelStateActions();
	const { setSidebarControlState, hideObjectSidebar } = useObjectSidebarActions();
	const isObjectSidebarShown = useIsObjectSidebarShown();
	const panel = usePanel();
	const { commentService } = useCommentService();
	const { showPanel: showDetailsPanel, hidePanel: hideDetailsPanel } = useDetailsPanelService();
	const { isDatabaseFeatureEnabled } = useIsDatabaseFeatureEnabled();
	const [flagService] = useState(() => new ContentTypesFlagService(flags));
	const [orgDataService] = useState(() => new DatabaseOrgDataService());
	const { publishDatabaseReferencingContent } = usePublishDatabaseReferencingContent();
	const isReactionsEnabled = useIsReactionsEnabled(contentId);

	// Initialize the Rovo content bridge
	useDatabaseRovoContentBridge({ enabled: true });

	useLayoutEffect(() => {
		if (orgId) {
			orgDataService.updateOrgId(orgId);
		}
	}, [orgId, orgDataService]);

	useEffect(() => {
		objectSidebarService.onChangeObjectSidebarShown({
			isOpen: isObjectSidebarShown,
			panelId: panel?.id,
		});
		objectSidebarService.onChangeObjectSidebarShown_LEGACY(isObjectSidebarShown);
	}, [isObjectSidebarShown, objectSidebarService, panel?.id]);

	useEffect(() => {
		// clean up referencing selected content when iframe unmounts
		return () => {
			publishDatabaseReferencingContent(undefined);
		};
	}, [publishDatabaseReferencingContent]);

	const onContentLoadSucceeded = useCallback(
		(attributes: ExperienceAttributes) => {
			experienceTracker.succeed({
				name: experienceName,
				attributes,
			});
			if (versionId) {
				experienceTracker.succeed({
					name: versionHistoryExperienceName,
					attributes,
				});
			}
			segmentMetric.stop();
		},
		[experienceTracker, experienceName, segmentMetric, versionId, versionHistoryExperienceName],
	);

	const onContentLoadFailed = useCallback(
		(error: Error, attributes: ExperienceAttributes) => {
			error.message = `Canvas Database error: ${error.message}`;

			experienceTracker.stopOnError({
				name: experienceName,
				attributes,
				error,
			});
			segmentMetric.cancel();
		},
		[experienceTracker, experienceName, segmentMetric],
	);

	const onCloseConfluenceSidePanel = useCallback(() => {
		hideSidePanel();
	}, [hideSidePanel]);

	const updateObjectSidebarControlState = useCallback(
		(isShown: boolean) => {
			if (isShown) {
				setSidebarControlState('shown');
			} else {
				setSidebarControlState('hidden');
			}
		},
		[setSidebarControlState],
	);

	return useMemo<DatabaseBridgeAPI>(
		() => ({
			/**
			 * Methods part of the shared content types bridge API.
			 * See @confluence/content-types-bridge for more details.
			 */
			confluenceAPI: {
				sidebar: {
					showDetailsPanel: () => showDetailsPanel(),
					hideDetailsPanel: () => hideDetailsPanel(),
					showCommentsPanel: (threadId?: string) => commentService.showPanel(threadId),
					hideCommentsPanel: () => commentService.hidePanel(),
					onConfluenceSidebarStateChange: (callback: (state: ConfluenceSidebarState) => void) =>
						objectSidebarService.registerChangeObjectSidebarShownHandler(callback),
				},
				comments: {
					getIsReactionsEnabled: () => isReactionsEnabled,
					registerOnReopenResolvedComment: (cb: ResolvedCommentHandler) =>
						commentService.registerOnReopenResolvedComment(cb),
					registerOnUpdateComment: (
						callback: (threadKey: string, actionType?: 'reply' | 'edit') => void,
					) => commentService.registerOnUpdateComment(callback),
					registerOnResolveComment: (callback: (threadKey: string) => void) =>
						commentService.registerOnResolveComment(callback),
					onCommentsPanelSelectThread: (callback) =>
						commentService.registerSelectThreadHandler(callback),
					selectCommentThread: (threadId?: string) => commentService.selectCommentThread(threadId),
					registerOnDeleteComment: (callback: (threadKey: string) => void) =>
						commentService.registerOnDeleteComment(callback),
					triggerCommentPanelRefresh: () => commentService.triggerCommentPanelRefresh(),
				},
				shell: {
					updateCollaborators: (collaborators: CanvasCollaborators) => {
						contentCollaboratorsService.updateActions(collaborators);
					},
					updateMainActions: () => {}, // not implemented
					updateBoardStatus: () => {}, // not implemented
					updateMoreActions: () => {}, // not implemented
					updateTitle: (title: string) => {
						contentTypesTitleServiceSingleton.updateTitle(title);
					},
					isFeatureActive: (name: string) => isDatabaseFeatureEnabled(name as DatabaseFeatureGate),
				},
				modals: {
					showFlag: (props: ContentTypesFlagProps) => {
						flagService.showFlag(props);
					},
					hideFlag: (id: string) => {
						flagService.hideFlag(id);
					},
				},
			},

			/**
			 * Methods below here are not part of the shared content types bridge API and are specific to Databases.
			 * Please consider moving them to the shared content types bridge API if they are useful for other content types.
			 * See @confluence/content-types-bridge for more details.
			 */
			onContentLoadSucceeded,
			onContentLoadFailed,
			updateBoardStatus: (status: ContentStatus) => {
				contentBoardStatusService.updateActions(status);
			},
			updateMoreActions: (items: MoreActions) => {
				contentMoreActionsService.updateActions(items);
			},
			onboardingService,
			objectSidebarMethods: {
				updateObjectSidebarControlState,
				hideObjectSidebarPanel: () => {
					// we hide an object sidebar panel in canvas when we open a db panel,
					// so we must also hide the object sidebar controls
					void hideObjectSidebar(() => updateObjectSidebarControlState(false));
				},
				registerChangeObjectSidebarShownHandler: (cb: (isShown?: boolean) => void) => {
					objectSidebarService.registerChangeObjectSidebarShownHandler_LEGACY(cb);
				},
			},
			onThemeChange: (callback: () => void) => {
				themingService.onThemeChange(callback);
			},
			registerOnReopenResolvedComment: (cb: ResolvedCommentHandler) => {
				commentService.registerOnReopenResolvedComment(cb);
			},
			getVersionName,
			onCloseConfluenceSidePanel,
			databaseSpotlightCloseRef,
			getOrgId: () => {
				return orgDataService.getId();
			},
			publishDatabaseReferencingContent,
			goToContent: () => {}, // not implemented
			goToHome: () => {}, // not implemented
			goToContainer: () => {}, // not implemented
			onTokenInit: () => {}, // not implemented
			onTokenChange: () => {}, // not implemented
			onContainerInit: () => {}, // not implemented
			onContainerChange: () => {}, // not implemented
			preloadToken: () => {}, // not implemented
			getToken: () => {}, // not implemented
			refreshToken: () => {}, // not implemented
			sessionData: {},
		}),
		[
			onContentLoadSucceeded,
			onContentLoadFailed,
			onboardingService,
			orgDataService,
			updateObjectSidebarControlState,
			hideObjectSidebar,
			themingService,
			getVersionName,
			onCloseConfluenceSidePanel,
			objectSidebarService,
			databaseSpotlightCloseRef,
			commentService,
			hideDetailsPanel,
			showDetailsPanel,
			flagService,
			isDatabaseFeatureEnabled,
			publishDatabaseReferencingContent,
			isReactionsEnabled,
		],
	);
};
